/**
 * Photographs on a journal day.
 *
 * The upload is the same three calls project documents use — presign, direct
 * PUT to S3, complete — with one addition: the browser also PUTs the downscale
 * it made, because the API has no image library and deliberately does not gain
 * one. See `lib/image-thumbnail.ts`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fileRefusal, type JournalPhoto } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import { makeThumbnail } from "@/lib/image-thumbnail";
import { putWithProgress } from "@/lib/upload";
import { UploadRefused, isRefusal, uploadFailure } from "@/lib/upload-failure";
import { journalKeys } from "./journals-api";

export const photoKeys = {
  list: (projectId: string, entryId: string) =>
    ["project", projectId, "journal", entryId, "photos"] as const,
};

/**
 * How often a still-undescribed photo is re-asked about.
 *
 * A vision call is seconds, and the caption a person typed is on screen the
 * whole time — this is only what makes the model's description appear without a
 * reload. Polling stops the moment nothing is pending.
 */
const DESCRIBE_POLL_MS = 4_000;

export function useJournalPhotos(
  projectId: string | undefined,
  entryId: string | undefined,
) {
  return useQuery({
    queryKey: photoKeys.list(projectId ?? "", entryId ?? ""),
    enabled: Boolean(projectId && entryId),
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (photo) => photo.descriptionStatus === "pending",
      )
        ? DESCRIBE_POLL_MS
        : false,
    queryFn: async (): Promise<JournalPhoto[]> => {
      const res = await api.journals.listPhotos({
        params: { id: projectId!, entryId: entryId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.photos;
    },
  });
}

export function useCaptionPhoto(projectId: string, entryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { photoId: string; caption: string | null }) => {
      const res = await api.journals.patchPhoto({
        params: { id: projectId, entryId, photoId: vars.photoId },
        body: { caption: vars.caption },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.photo;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: photoKeys.list(projectId, entryId),
      });
      // The caption is the Brain entry's title, so that list is stale too.
      void queryClient.invalidateQueries({
        queryKey: ["project", projectId, "brain"],
      });
    },
  });
}

export function useDeletePhoto(projectId: string, entryId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (photoId: string) => {
      const res = await api.journals.removePhoto({
        params: { id: projectId, entryId, photoId },
      });
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: photoKeys.list(projectId, entryId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["project", projectId, "brain"],
      });
    },
  });
}

/**
 * Get the day's record, or open it.
 *
 * Idempotent server-side, which is what lets photographs be dropped on a date
 * nobody has written up yet — the ordinary case, since pictures are taken
 * during the day and the record is written at the end of it.
 */
export function useOpenDay(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (date: string) => {
      const res = await api.journals.openDay({
        params: { id: projectId, date },
        body: {},
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.entry;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: journalKeys.list(projectId),
      });
    },
  });
}

// Where `AuthedImage` fetches a photo from, and its downscale — both built in
// `@gb/contracts/paths` now, alongside the Nest route patterns the controller
// is decorated with. Re-exported under the console's own names so nothing that
// imports them changes.
export {
  journalPhotoContentPath as photoContentPath,
  journalPhotoThumbnailPath as photoThumbnailPath,
} from "@gb/contracts";

// ------------------------------------------------------------------
// Uploads
// ------------------------------------------------------------------

export type PhotoUpload = {
  /** Client-side id — the server's photo id only exists after presigning. */
  key: string;
  /** The server's id, once presigned. What the row is handed over on. */
  photoId?: string;
  filename: string;
  status: "queued" | "uploading" | "processing" | "done" | "error";
  /** 0–100. Meaningful while `uploading`. */
  progress: number;
  error?: string;
  /** Refused, and would be refused again: no Retry (`architecture/0031`). */
  refused?: boolean;
  /** A local preview, shown in the grid before the upload finishes. */
  objectUrl: string;
  file: File;
};

/** Concurrent uploads in flight. The rest queue. */
const MAX_CONCURRENT = 3;

let counter = 0;
function nextKey(): string {
  counter += 1;
  return `photo-${counter}`;
}

/**
 * Manage a queue of photo uploads for one day.
 *
 * Deliberately the same shape as `useUploads` in `documents-api.ts`, including
 * the two guards that file was written to fix — the concurrency counter in a
 * ref, and the `started` set that stops two `pump` calls landing before the
 * first state commit from uploading the same file twice. Dropping eight photos
 * at once is exactly the case that exposes both.
 *
 * What differs: each file is downscaled first, and a *local* object URL is kept
 * so the grid shows the picture immediately rather than a filename and a bar.
 */
export function useJournalPhotoUploads(
  projectId: string | undefined,
  entryId: string | undefined,
  /**
   * Called once per photograph, at the moment it becomes a filed row.
   *
   * The composer needs this because a note has no id until it is saved: the
   * pictures go up against the day first, and this is how the form learns
   * which ones to claim for the note it is about to file.
   */
  onFiled?: (photoId: string) => void,
) {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<PhotoUpload[]>([]);
  const inFlight = useRef(0);
  const started = useRef(new Set<string>());
  // Held in a ref so the handover effect does not re-run — and re-report — every
  // time the caller passes a fresh closure, which an inline arrow does on every
  // render.
  const onFiledRef = useRef(onFiled);
  onFiledRef.current = onFiled;

  // The same query the grid renders — an extra observer, not an extra fetch.
  // A finished upload has to hand its tile over to the photograph it became,
  // and the only honest signal for that is the photograph being in the list.
  const { data: photos } = useJournalPhotos(projectId, entryId);
  const filed = useMemo(
    () => new Set((photos ?? []).map((p) => p.id)),
    [photos],
  );

  const patch = useCallback((key: string, next: Partial<PhotoUpload>) => {
    setUploads((current) =>
      current.map((u) => (u.key === key ? { ...u, ...next } : u)),
    );
  }, []);

  // `run` starts the next queued upload when it finishes and `pump` starts
  // `run` — a genuine cycle. The ref breaks it without either closure going
  // stale, which a plain mutual reference would.
  const pumpRef = useRef<() => void>(() => {});

  const run = useCallback(
    async (upload: PhotoUpload): Promise<void> => {
      if (!projectId || !entryId) return;
      inFlight.current += 1;
      patch(upload.key, { status: "uploading", progress: 0, error: undefined });

      try {
        // Before presigning, so the request can say whether a second URL is
        // wanted. A browser that cannot decode the format returns null and the
        // photo simply has no downscale.
        const thumbnail = await makeThumbnail(upload.file);

        const presign = await api.journals.photoUploadUrl({
          params: { id: projectId, entryId },
          body: {
            filename: upload.file.name,
            mimeType: upload.file.type || "application/octet-stream",
            sizeBytes: upload.file.size,
            withThumbnail: Boolean(thumbnail),
            ...(thumbnail
              ? { widthPx: thumbnail.widthPx, heightPx: thumbnail.heightPx }
              : {}),
          },
        });
        if (isRefusal(presign.status)) throw new UploadRefused(errorMessage(presign));
        if (presign.status !== 201) throw new Error(errorMessage(presign));
        patch(upload.key, { photoId: presign.body.photoId });

        await putWithProgress(presign.body.uploadUrl, upload.file, (percent) =>
          patch(upload.key, { progress: percent }),
        );

        // The thumbnail is best-effort in the strongest sense: a failure here
        // must not fail the photograph, whose bytes are already in the bucket.
        // `complete` re-heads it and clears the column if it never landed.
        if (thumbnail && presign.body.thumbnailUploadUrl) {
          await putWithProgress(
            presign.body.thumbnailUploadUrl,
            new File([thumbnail.blob], "thumb.jpg", { type: "image/jpeg" }),
            () => undefined,
          ).catch(() => undefined);
        }

        patch(upload.key, { status: "processing", progress: 100 });

        const complete = await api.journals.completePhoto({
          params: { id: projectId, entryId, photoId: presign.body.photoId },
          body: {},
        });
        if (isRefusal(complete.status)) throw new UploadRefused(errorMessage(complete));
        if (complete.status !== 200) throw new Error(errorMessage(complete));

        patch(upload.key, { status: "done" });
        // The row now stands in for a photograph that exists. It is cleared by
        // the handover below, when this invalidation brings that photograph
        // back — not on a timer racing the same refetch.
        void queryClient.invalidateQueries({
          queryKey: photoKeys.list(projectId, entryId),
        });
        void queryClient.invalidateQueries({
          queryKey: ["project", projectId, "brain"],
        });
      } catch (error) {
        patch(upload.key, { status: "error", ...uploadFailure(error) });
      } finally {
        inFlight.current -= 1;
        pumpRef.current();
      }
    },
    [projectId, entryId, patch, queryClient],
  );

  const pump = useCallback(() => {
    // Read inside `setUploads` rather than from a captured value, so it sees
    // the state React has actually committed — eight files dropped at once
    // arrive in one batch, and a stale read would start the same one twice.
    setUploads((current) => {
      const starting = current
        .filter((u) => u.status === "queued" && !started.current.has(u.key))
        .slice(0, Math.max(0, MAX_CONCURRENT - inFlight.current));
      for (const upload of starting) {
        started.current.add(upload.key);
        void run(upload);
      }
      return current;
    });
  }, [run]);

  pumpRef.current = pump;

  const add = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      setUploads((current) => [
        ...current,
        ...files.map((file) => {
          // The name and size rules, before any request (`architecture/0031`).
          // The type is left to the server: a photograph has its own list, and
          // a HEIC its own sentence.
          const refused = fileRefusal({ filename: file.name, sizeBytes: file.size });
          return {
            key: nextKey(),
            filename: file.name,
            ...(refused
              ? { status: "error" as const, error: refused.reason, refused: true }
              : { status: "queued" as const }),
            progress: 0,
            objectUrl: URL.createObjectURL(file),
            file,
          };
        }),
      ]);
      // After the state commit, so the queued rows exist for `pump` to find.
      setTimeout(pump, 0);
    },
    [pump],
  );

  const retry = useCallback(
    (key: string) => {
      // Clear the start guard, or the row goes back to `queued` and is never
      // picked up again.
      started.current.delete(key);
      patch(key, { status: "queued", error: undefined, refused: false, progress: 0 });
      setTimeout(pump, 0);
    },
    [patch, pump],
  );

  const dismiss = useCallback((key: string) => {
    started.current.delete(key);
    setUploads((current) => {
      const going = current.find((u) => u.key === key);
      if (going) URL.revokeObjectURL(going.objectUrl);
      return current.filter((u) => u.key !== key);
    });
  }, []);

  // A row whose photograph is in the list has nothing left to stand in for.
  // Held back from the render below rather than deleted here, so the two never
  // overlap in a painted frame; this effect only frees the object URL and the
  // state afterwards. Status is not consulted on purpose — a `complete` whose
  // response was lost leaves an errored row for a photo that is safely filed,
  // and that row should go too.
  useEffect(() => {
    const handed = uploads.filter((u) => u.photoId && filed.has(u.photoId));
    if (handed.length === 0) return;
    for (const upload of handed) {
      URL.revokeObjectURL(upload.objectUrl);
      onFiledRef.current?.(upload.photoId!);
    }
    const gone = new Set(handed.map((u) => u.key));
    setUploads((current) => current.filter((u) => !gone.has(u.key)));
  }, [uploads, filed]);

  return {
    uploads: uploads.filter((u) => !(u.photoId && filed.has(u.photoId))),
    add,
    retry,
    dismiss,
  };
}
